import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Users, Eye, Scale, FileSignature, HeartHandshake,
  Lightbulb, HandHeart, Handshake, Gift,
} from "lucide-react";
import { useLang } from "../LanguageContext";
import { DonateButton } from "../components/DonateButton";

const OBSERVE_ICONS = [Eye, Users, Scale, HeartHandshake, FileSignature];
const FEATURE_IMG =
  "https://customer-assets.emergentagent.com/job_ecole-pleine-nature/artifacts/epgipe49_vallee.jpg";

export default function SEngager() {
  const { t } = useLang();
  const forWho = t("immersions.for_who") || [];
  const observe = t("immersions.observe") || [];
  const howSteps = t("immersions.how_steps") || [];

  const forms = [
    { id: "proposer", icon: Lightbulb, title: t("engage.propose_title"), text: t("engage.propose_text"), cta: t("engage.propose_cta"), sujet: "benevolat" },
    { id: "benevole", icon: HandHeart, title: t("engage.benevole_title"), text: t("engage.benevole_text"), cta: t("engage.benevole_cta"), sujet: "benevolat" },
    { id: "partenaire", icon: Handshake, title: t("engage.partenaire_title"), text: t("engage.partenaire_text"), cta: t("engage.partenaire_cta"), sujet: "partenariat" },
    { id: "soutenir", icon: Gift, title: t("engage.soutenir_title"), text: t("engage.soutenir_text"), cta: t("engage.soutenir_cta"), sujet: "soutien" },
  ];

  return (
    <div data-testid="page-sengager">
      <section className="container-page pt-16 sm:pt-24 pb-10">
        <p className="eyebrow mb-4">{t("engage.eyebrow")}</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink max-w-3xl leading-tight">
          {t("engage.title")}
        </h1>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
          {t("engage.intro")}
        </p>
        <p className="mt-3 text-sm text-ink-muted">
          {t("engage.adult_note_a")}
          <Link to="/inscrire" className="text-forest underline underline-offset-4">
            {t("engage.adult_note_b")}
          </Link>
          {t("engage.adult_note_c")}
        </p>
      </section>

      <section className="container-page pb-6" data-testid="donate-banner">
        <div className="rounded-[2rem] bg-forest text-cream-50 px-8 sm:px-12 py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cream-100/70 mb-1.5">
              {t("engage.soutenir_title")}
            </p>
            <h2 className="font-heading text-xl sm:text-2xl font-medium leading-snug max-w-xl text-cream-50">
              {t("engage.donate_banner_text")}
            </h2>
          </div>
          <DonateButton
            className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 bg-cream-50 text-forest-deep font-medium hover:bg-cream-100 transition-colors shrink-0"
            testId="sengager-donate-top"
          />
        </div>
      </section>

      <section className="container-page pb-8">
        <div className="max-w-xs mx-auto rounded-[2rem] overflow-hidden shadow-soft bg-cream-100">
          <img
            src={FEATURE_IMG}
            alt="École Pleine Nature — sortie en montagne dans les Pyrénées"
            className="w-full h-auto block"
            loading="lazy"
          />
        </div>
      </section>

      {/* Immersion & stage */}
      <section id="immersion" className="container-page py-8 scroll-mt-24">
        <h2 className="font-heading text-3xl font-medium text-ink mb-8">
          {t("engage.visit_title")}
        </h2>
        <div className="grid lg:grid-cols-12 gap-7">
          <div className="lg:col-span-5 card-soft p-8">
            <h3 className="font-heading text-2xl font-semibold text-ink mb-4">
              {t("immersions.for_who_title")}
            </h3>
            <ul className="space-y-2.5">
              {forWho.map((f) => (
                <li key={f} className="flex items-start gap-3 text-ink-muted">
                  <span className="w-1.5 h-1.5 rounded-full bg-earth mt-2.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 card-soft p-8">
            <h3 className="font-heading text-2xl font-semibold text-ink mb-4">
              {t("immersions.observe_title")}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {observe.map((label, i) => {
                const Icon = OBSERVE_ICONS[i] || Eye;
                return (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-2xl bg-cream-100 px-4 py-3 border border-cream-300"
                  >
                    <Icon size={18} className="text-forest shrink-0" />
                    <span className="text-sm text-ink">{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="container-page py-8">
        <div className="rounded-[2rem] bg-forest-light/60 border border-forest-light px-8 sm:px-12 py-10 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <p className="eyebrow mb-2">{t("immersions.adult_eyebrow")}</p>
            <h3 className="font-heading text-2xl font-semibold text-ink">
              {t("immersions.adult_title")}
            </h3>
            <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">
              {t("immersions.adult_text")}
            </p>
          </div>
          <div className="text-right">
            <div className="font-heading text-4xl text-forest font-medium">
              {t("immersions.adult_price")}
            </div>
            <div className="text-sm text-ink-muted">{t("immersions.adult_per")}</div>
          </div>
        </div>
      </section>

      <section className="container-page py-8">
        <div className="grid md:grid-cols-2 gap-5">
          <div id="stage" className="card-soft p-8 scroll-mt-24">
            <h3 className="font-heading text-xl font-semibold text-ink mb-3">
              {t("immersions.stages_title")}
            </h3>
            <p className="text-ink-muted leading-relaxed">{t("immersions.stages_text")}</p>
          </div>
          <div id="civic" className="card-soft p-8 scroll-mt-24">
            <h3 className="font-heading text-xl font-semibold text-ink mb-3">
              {t("immersions.civic_title")}
            </h3>
            <p className="text-ink-muted leading-relaxed">{t("immersions.civic_text")}</p>
          </div>
        </div>
      </section>

      <section className="container-page py-8">
        <div className="card-soft p-8 sm:p-12">
          <h2 className="font-heading text-2xl font-semibold text-ink mb-4">
            {t("immersions.how_title")}
          </h2>
          <ol className="space-y-3 text-ink-muted">
            {howSteps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-accent text-2xl text-earth leading-none">{i + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Other ways to get involved */}
      <section className="container-page py-8">
        <h2 className="font-heading text-3xl font-medium text-ink mb-8">
          {t("engage.forms_title")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {forms.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                id={f.id}
                className="card-soft p-8 flex flex-col scroll-mt-24"
                data-testid={`engage-${f.id}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-forest-light grid place-items-center mb-5 text-forest">
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-ink mb-2">{f.title}</h3>
                <p className="text-ink-muted leading-relaxed flex-1">{f.text}</p>
                {f.id === "soutenir" ? (
                  <>
                    <p className="text-xs text-ink-muted/90 mt-3 leading-relaxed border-l-2 border-forest-light pl-3">
                      {t("engage.soutenir_tax")}
                    </p>
                    <DonateButton
                      className="btn-primary mt-6 self-start"
                      label={f.cta}
                      withIcon={false}
                      testId={`engage-${f.id}-cta`}
                    />
                  </>
                ) : (
                  <Link
                    to={`/contact?sujet=${f.sujet}`}
                    className="btn-outline mt-6 self-start"
                    data-testid={`engage-${f.id}-cta`}
                  >
                    {f.cta} <ArrowRight size={18} />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="container-page py-16">
        <div className="rounded-[2.5rem] bg-forest text-cream-50 px-8 sm:px-14 py-14 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-medium text-cream-50">
            {t("immersions.final_title")}
          </h3>
          <Link
            to="/contact?sujet=immersion"
            className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-cream-50 text-forest-deep font-medium hover:bg-cream-100 transition-colors"
            data-testid="cta-engager-contact"
          >
            {t("immersions.final_cta")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
